import * as THREE from 'three';
import { WEAPONS } from './WeaponsData.js';
import { EVENTS } from '../core/EventBus.js';
import { PLAYER, TEAM } from '../core/Constants.js';

/**
 * WeaponSystem - secao 4.13 do ARCHITECTURE.md.
 *
 * Dono do disparo, da municao, da recarga, da troca e da mira do JOGADOR.
 * Nao desenha nada (isso e do ViewModel e do FX), nao move a camera (isso e do
 * CameraRig) e nao decide dano por hitbox (isso e da Ballistics).
 *
 * ===========================================================================
 * COMO O RECUO CHEGA NA CAMERA (leia antes de mexer no CameraRig)
 * ===========================================================================
 * Sao DUAS coisas diferentes, de proposito, e elas NAO se sobrepoem:
 *
 *   1. getRecoilOffset() -> THREE.Vector2 em RADIANOS, lido A CADA FRAME.
 *      E o deslocamento ACUMULADO do padrao de recuo: o quanto a mira ja subiu
 *      e derivou por causa da rajada em andamento. O CameraRig deve SOMAR isso
 *      ao yaw/pitch vindos do mouse, na hora de montar a rotacao final:
 *          yawFinal   = yawMouse   + offset.x
 *          pitchFinal = pitchMouse + offset.y
 *      Convencao (mesma do three, rotation.order 'YXZ'):
 *          offset.y (pitch) > 0  -> camera olha para CIMA
 *          offset.x (yaw)   > 0  -> camera gira para a ESQUERDA (rotation.y)
 *      Este valor volta sozinho para zero quando o jogador solta o gatilho.
 *
 *   2. cameraRig.addRecoil(pitchRad, yawRad) -> IMPULSO, so no instante do tiro.
 *      E o coice visual seco, que o CameraRig amortece com mola propria. Ele
 *      NAO desloca a mira de verdade; e tempero. Usa a mesma convencao de sinal.
 *
 * Quem atira e a camera: `_fire()` usa a direcao mundial de `ctx.camera`, que ja
 * contem o offset aplicado pelo CameraRig no frame anterior. Por isso o sistema
 * NAO soma o recuo de novo na direcao da bala - isso contaria o recuo duas vezes.
 *
 * ===========================================================================
 * MAQUINA DE ESTADOS
 * ===========================================================================
 * Sao duas maquinas paralelas, porque no mundo real elas sao independentes
 * (da para estar mirando E recarregando; da para atirar mirando):
 *
 *   this.state    'idle' | 'firing' | 'reloading' | 'switching'   (acao)
 *   this.aimState 'hip'  | 'ads'                                   (mira)
 *
 * Regras de cancelamento (definidas aqui e valem para o jogo inteiro):
 *   - TROCAR DE ARMA cancela a recarga. A municao NAO e creditada (a recarga
 *     nao terminou). E a unica forma de cancelar - e tecnica valida de jogo.
 *   - MIRAR NAO cancela a recarga. O pedido de mira fica guardado: assim que a
 *     recarga acaba, se o botao direito ainda estiver pressionado, a arma entra
 *     na mira sozinha. Isso evita o exploit de "recarrega e cancela com o botao
 *     direito" e mantem o comportamento previsivel.
 *   - CORRER abaixa a arma: nao atira e nao mira. Ao sair da corrida ainda ha
 *     `sprintOutTime` de arma subindo antes do primeiro tiro.
 *   - O gatilho durante a recarga nao faz nada (nem clique seco).
 */

const DEG = Math.PI / 180;

/** Teto de tiros por frame. Protege contra queda de fps virar rajada instantanea. */
const MAX_SHOTS_PER_FRAME = 3;

/** Abaixo disso o recuo acumulado e considerado zero (evita cauda infinita). */
const RECOIL_EPSILON = 2e-5;

/** Quanto da penalidade de movimento a mira NAO consegue apagar. */
const ADS_MOVE_PENALTY_KEEP = 0.45;

/** Janela, em multiplos do intervalo de tiro, em que ainda estamos "na rajada". */
const BURST_WINDOW_FACTOR = 1.75;

/** Intervalo minimo entre dois cliques secos, para o som nao virar metralhadora. */
const DRY_COOLDOWN = 0.28;

// ---------------------------------------------------------------------------
// Temporarios de modulo. ZERO alocacao dentro de update() - ver secao 6 do
// ARCHITECTURE.md. Nenhum destes vetores pode ser guardado por outro sistema.
// ---------------------------------------------------------------------------
const _origin = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _muzzle = new THREE.Vector3();
const _camPos = new THREE.Vector3();
const _camQuat = new THREE.Quaternion();

/**
 * Parametros reaproveitados nas chamadas a outros sistemas. E seguro reusar
 * porque `ballistics.fire()` e `fx.muzzleFlash()` consomem o objeto de forma
 * SINCRONA, dentro da propria chamada. Quem receber isto nao pode guardar a
 * referencia para o frame seguinte.
 */
const _fireParams = {
  origin: null, dir: null, weapon: null, shooter: null, team: 0,
  spread: 0, damage: 0, range: 0, pellets: 1,
  penetration: 0, falloff: null, fromPlayer: true
};
const _flashParams = { position: null, direction: null, scale: 1 };

const _warned = new Set();
/** Avisa uma unica vez por chave. Sistema que falha degrada em silencio. */
function warnOnce(key, err) {
  if (_warned.has(key)) return;
  _warned.add(key);
  console.warn(`[WeaponSystem] falha em ${key} (ignorada daqui em diante):`, err);
}

function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }
function lerp(a, b, t) { return a + (b - a) * t; }
function moveTowards(from, to, step) {
  if (from < to) { const v = from + step; return v > to ? to : v; }
  if (from > to) { const v = from - step; return v < to ? to : v; }
  return to;
}

export class WeaponSystem {
  constructor(ctx) {
    this.ctx = ctx;

    /** Arsenal completo, na ordem dos slots 1/2/3. @type {Array<object>} */
    this.weapons = WEAPONS;

    /** Municao por arma. Guardada fora da arma para o dado da arma ficar imutavel. */
    this._slots = WEAPONS.map((w) => ({ ammo: w.magazine, reserve: w.reserve }));

    this._index = 0;
    this._lastIndex = WEAPONS.length > 1 ? 1 : 0;
    this._pendingIndex = 0;

    // --- estado publico ---
    this.state = 'idle';
    this.aimState = 'hip';
    this.isADS = false;
    this.adsProgress = 0;
    this.spread = 0.01;
    this.enabled = false;          // ligado pelo GAME_STATE 'playing'

    // --- estado interno ---
    this._recoil = new THREE.Vector2();  // offset acumulado, radianos (x=yaw, y=pitch)
    this._bloom = 0;                     // dispersao acumulada por tiro, radianos
    this._shotIndex = 0;                 // posicao no padrao de recuo
    this._fireTimer = 0;                 // acumulador de cadencia (segundos)
    this._sinceShot = 999;
    this._dryTimer = 0;
    this._stateTimer = 0;
    this._switchOut = 0;
    this._switchIn = 0;
    this._swapped = true;
    this._reloadEmpty = false;
    this._adsProgress = 0;
    this._adsPrev = false;
    this._triggerHeld = false;
    this._triggerReleased = true;
    this._sprintBlocked = false;
    this._raiseTimer = 0;
    this._gameState = null;
    this._bootstrapped = false;
    this._ready = false;
    this._offHandlers = [];
  }

  // =========================================================================
  // Ciclo de vida
  // =========================================================================

  async init() {
    const ev = this.ctx.events;
    this._offHandlers.push(ev.on(EVENTS.GAME_STATE, (p) => this._onGameState(p)));
    this._offHandlers.push(ev.on(EVENTS.PLAYER_DIED, () => this._onDeath()));
    this.spread = this.getSpread();
    this._ready = true;
  }

  dispose() {
    for (const off of this._offHandlers) {
      try { off(); } catch (err) { warnOnce('dispose', err); }
    }
    this._offHandlers.length = 0;
    this._ready = false;
  }

  // =========================================================================
  // API publica (contrato da secao 4.13)
  // =========================================================================

  /** Definicao da arma em uso. Somente leitura. */
  get current() { return this.weapons[this._index]; }

  /** Indice do slot em uso (0..2). */
  get index() { return this._index; }

  /** Balas no pente da arma atual. */
  get ammo() { const s = this._slots[this._index]; return s ? s.ammo : 0; }
  set ammo(v) { const s = this._slots[this._index]; if (s) s.ammo = Math.max(0, v | 0); }

  /** Balas em reserva da arma atual. */
  get reserve() { const s = this._slots[this._index]; return s ? s.reserve : 0; }
  set reserve(v) { const s = this._slots[this._index]; if (s) s.reserve = Math.max(0, v | 0); }

  /** Capacidade do pente da arma atual (o HUD usa para o estado critico). */
  get magazine() { return this.current ? this.current.magazine : 0; }

  /**
   * Offset ACUMULADO do recuo, em radianos. Vector2 REUTILIZADO: leia os campos,
   * nao guarde a referencia nem mute o objeto.
   *   x = yaw   (somar em camera.rotation.y; positivo gira para a esquerda)
   *   y = pitch (somar em camera.rotation.x; positivo olha para cima)
   */
  getRecoilOffset() { return this._recoil; }

  /** Atalhos sem ambiguidade de eixo, para quem preferir numeros soltos. */
  getRecoilPitch() { return this._recoil.y; }
  getRecoilYaw() { return this._recoil.x; }

  /**
   * Dispersao ATUAL em radianos (meia-abertura do cone), ja considerando estado
   * do jogador, mira, rajada em andamento e bonus de primeiro tiro.
   * O HUD abre a mira com isto; a Ballistics dispersa a bala com isto.
   */
  getSpread() {
    const w = this.current;
    if (!w) return 0.01;
    const s = w.spread;
    const c = this.ctx.controller;
    const grounded = c ? c.grounded !== false : true;
    const crouching = !!(c && c.crouching);
    const speed = this._horizontalSpeed();

    // referencia "parado" do estado atual: e o piso que a mira consegue alcancar
    const still = crouching ? s.crouch : s.idle;

    let base;
    if (!grounded) {
      base = s.air;
    } else {
      base = lerp(still, s.walk, clamp01(speed / PLAYER.WALK_SPEED));
      if (speed > PLAYER.WALK_SPEED) {
        const range = Math.max(0.1, PLAYER.SPRINT_SPEED - PLAYER.WALK_SPEED);
        base = lerp(base, s.sprint, clamp01((speed - PLAYER.WALK_SPEED) / range));
      }
    }

    const a = this._adsProgress;
    if (a > 0) {
      // A mira leva a dispersao ao valor de ADS, mas so ABRANDA o que veio do
      // movimento ou do pulo - mirar no ar continua sendo ideia ruim.
      const penalty = base > still ? base - still : 0;
      base = lerp(base, s.ads + penalty * ADS_MOVE_PENALTY_KEEP, a);
    }

    let spread = base + this._bloom * lerp(1, w.adsBloomScale, a);

    // Primeiro tiro do padrao, com os pes no chao: quase perfeito. E a regra que
    // faz rajada curta valer a pena e da ao DMR o "um clique, um acerto".
    if (this._shotIndex === 0 && grounded && !this._sprintBlocked) {
      spread *= w.firstShotAccuracy;
    }

    return spread > 0 ? spread : 0;
  }

  /**
   * A arma esta em condicao de disparar AGORA? Nao considera o gatilho: para
   * semiautomatica ainda e preciso soltar e apertar de novo.
   */
  canFire() {
    if (!this._ready || !this.enabled) return false;
    if (!this._isAlive()) return false;
    if (this.state === 'reloading' || this.state === 'switching') return false;
    if (this._sprintBlocked || this._raiseTimer > 0) return false;
    return this.ammo > 0 && this._fireTimer <= 0;
  }

  /** Progresso da mira, 0 = quadril, 1 = totalmente na mira. */
  getAdsProgress() { return this._adsProgress; }

  /** FOV alvo da camera considerando a mira. O CameraRig interpola ate aqui. */
  getAdsFov() {
    const w = this.current;
    const base = this.ctx.settings.fov;
    if (!w) return base;
    return lerp(base, w.adsFov, this._adsProgress);
  }

  /**
   * Multiplicador de sensibilidade do mouse. O CameraRig passa isto em
   * `input.consumeMouseDelta(scale)` para a mira nao ficar nervosa no zoom.
   */
  getSensitivityScale() {
    const w = this.current;
    if (!w) return 1;
    const target = w.adsSensitivityScale * (this.ctx.settings.adsSensitivityScale ?? 1);
    return lerp(1, target, this._adsProgress);
  }

  /** 0..1 durante a recarga (0 fora dela). Util para barra no HUD. */
  getReloadProgress() {
    if (this.state !== 'reloading') return 0;
    const w = this.current;
    const total = this._reloadEmpty ? w.reloadEmptyTime : w.reloadTime;
    if (total <= 0) return 1;
    return clamp01(1 - this._stateTimer / total);
  }

  /** 0..1 durante a troca de arma (0 fora dela). */
  getSwitchProgress() {
    if (this.state !== 'switching') return 0;
    const total = this._switchOut + this._switchIn;
    if (total <= 0) return 1;
    return clamp01(1 - this._stateTimer / total);
  }

  /** Escreve em `out` a posicao mundial da boca do cano. Devolve `out`. */
  getMuzzlePosition(out) {
    return this._resolveMuzzle(out || _muzzle, this.ctx.camera);
  }

  /** Inicia a recarga. Devolve false se nao havia o que recarregar. */
  reload() { return this._startReload(); }

  /** Troca de arma por indice (0..2) ou por id ('kr7'). */
  switchTo(which) {
    let i = -1;
    if (typeof which === 'number') i = which;
    else if (typeof which === 'string') {
      for (let k = 0; k < this.weapons.length; k++) if (this.weapons[k].id === which) i = k;
    }
    return this._requestSwitch(i);
  }

  /** Tecla Q: volta para a ultima arma usada (nao e "a proxima da lista"). */
  nextWeapon() { return this._requestSwitch(this._lastIndex); }

  /** Devolve todo o arsenal ao estado inicial. Chamado no inicio de cada partida. */
  resetLoadout() {
    for (let i = 0; i < this._slots.length; i++) {
      this._slots[i].ammo = this.weapons[i].magazine;
      this._slots[i].reserve = this.weapons[i].reserve;
    }
    this._index = 0;
    this._lastIndex = this.weapons.length > 1 ? 1 : 0;
    this._pendingIndex = 0;
    this.state = 'idle';
    this._stateTimer = 0;
    this._swapped = true;
    this._resetWeaponState();
    this._adsProgress = 0;
    this.adsProgress = 0;
    this.isADS = false;
    this.aimState = 'hip';
    this._adsPrev = false;
    this._raiseTimer = 0;
    try { this.ctx.viewModel?.setWeapon?.(this.current); } catch (err) { warnOnce('viewModel.setWeapon', err); }
  }

  /** Municao extra na reserva da arma atual (caixa de municao, recompensa de onda). */
  giveAmmo(amount, weaponIndex) {
    const i = weaponIndex == null ? this._index : weaponIndex;
    const slot = this._slots[i];
    if (!slot || !(amount > 0)) return 0;
    slot.reserve += Math.floor(amount);
    return slot.reserve;
  }

  // =========================================================================
  // Laco de frame
  // =========================================================================

  update(dt) {
    if (!this._ready) return;
    // Rede de seguranca da regra 8: um erro aqui vira aviso, nunca derruba o frame.
    try {
      this._update(dt);
    } catch (err) {
      warnOnce('update', err);
    }
  }

  _update(dt) {
    if (!this._bootstrapped) this._bootstrap();

    const alive = this._isAlive();
    const active = this.enabled && alive;

    this._sinceShot += dt;
    if (this._dryTimer > 0) this._dryTimer -= dt;
    if (this._raiseTimer > 0) this._raiseTimer -= dt;

    // 1. estados com duracao propria correm mesmo sem input (recarga, troca)
    this._tickTimedStates(dt);

    // 2. corrida abaixa a arma
    const blocked = this._updateSprintBlock();

    // 3. entrada do jogador
    if (active) {
      this._readSwitchInput();
      if (this.state !== 'switching') this._readReloadInput();
    }

    // 4. mira (depende do estado, entao vem depois da recarga/troca)
    this._updateAds(dt, active, blocked);

    // 5. gatilho
    this._updateTrigger(dt, active, blocked);

    // 6. o recuo e a dispersao voltam ao lugar
    this._updateRecoil(dt);
    this._updateBloom(dt);

    // 7. o padrao volta ao tiro 1 depois de um respiro - e o que garante que a
    //    proxima rajada comece precisa de novo
    const rec = this.current.recoilRecovery;
    if (this._shotIndex > 0 && !this._triggerHeld && this._sinceShot > rec.resetTime) {
      this._shotIndex = 0;
    }

    // 8. sair do estado 'firing' quando a rajada esfria
    if (this.state === 'firing' && this._sinceShot > this._shotInterval() * 1.25) {
      this.state = 'idle';
    }

    // valor publicado, para o HUD nao ter que recalcular
    this.spread = this.getSpread();
  }

  /**
   * Primeira volta do laco. O ViewModel so existe depois de nos em main.js,
   * entao a arma inicial e anunciada aqui, e nao no init().
   */
  _bootstrap() {
    this._bootstrapped = true;
    const w = this.current;
    try { this.ctx.viewModel?.setWeapon?.(w); } catch (err) { warnOnce('viewModel.setWeapon', err); }
    this.ctx.events.emit(EVENTS.WEAPON_SWITCH, {
      weapon: w, previous: null, outTime: 0, inTime: 0, initial: true
    });
  }

  // =========================================================================
  // Estados com duracao
  // =========================================================================

  _tickTimedStates(dt) {
    if (this.state === 'switching') {
      this._stateTimer -= dt;
      // A arma so troca de verdade no fundo do movimento (fora da tela).
      if (!this._swapped && this._stateTimer <= this._switchIn) {
        this._swapped = true;
        this._commitSwitch();
      }
      if (this._stateTimer <= 0) {
        this._stateTimer = 0;
        this.state = 'idle';
      }
      return;
    }

    if (this.state === 'reloading') {
      this._stateTimer -= dt;
      if (this._stateTimer <= 0) this._finishReload();
    }
  }

  _updateSprintBlock() {
    const c = this.ctx.controller;
    let blocked = false;
    if (c && c.sprinting) {
      // Exige velocidade real: um flag de sprint com o jogador parado nunca
      // pode travar a arma (o controller e escrito por outro sistema).
      blocked = this._horizontalSpeed() > PLAYER.WALK_SPEED * 0.6;
    }
    if (this._sprintBlocked && !blocked) {
      // saiu da corrida: a arma leva um instante para voltar a posicao de tiro
      this._raiseTimer = this.current.sprintOutTime || 0;
    }
    this._sprintBlocked = blocked;
    return blocked || this._raiseTimer > 0;
  }

  // =========================================================================
  // Mira
  // =========================================================================

  _updateAds(dt, active, blocked) {
    const w = this.current;
    let want = active && !blocked && this.ctx.input.isMouseDown(2);

    // Mirar NAO cancela recarga nem troca: o pedido fica guardado e se aplica
    // sozinho quando a acao terminar, porque o botao continua pressionado.
    if (this.state === 'reloading' || this.state === 'switching') want = false;

    this.isADS = want;
    this.aimState = want ? 'ads' : 'hip';

    // sair da mira e um pouco mais rapido que entrar - largar a mira e reflexo
    const rate = want
      ? 1 / Math.max(0.02, w.adsTime)
      : 1 / Math.max(0.02, w.adsTime * 0.8);
    this._adsProgress = moveTowards(this._adsProgress, want ? 1 : 0, rate * dt);
    this.adsProgress = this._adsProgress;

    if (want !== this._adsPrev) {
      this._adsPrev = want;
      this.ctx.events.emit(EVENTS.WEAPON_ADS, { weapon: w, active: want });
      this.ctx.events.emit(EVENTS.SFX, {
        name: want ? w.sound.adsIn : w.sound.adsOut,
        volume: 0.45
      });
    }
  }

  // =========================================================================
  // Gatilho e cadencia
  // =========================================================================

  _shotInterval() {
    const w = this.current;
    return 60 / Math.max(1, w.rpm);
  }

  _updateTrigger(dt, active, blocked) {
    const input = this.ctx.input;
    const w = this.current;
    const held = active && !blocked && input.isMouseDown(0);

    this._triggerHeld = held;
    if (!held) this._triggerReleased = true;

    // Acumulador de cadencia: guarda no maximo um intervalo de credito enquanto
    // o gatilho esta apertado (mantem o rpm honesto em fps baixo) e zera quando
    // nao da para atirar. Sem esse zeramento, segurar o gatilho durante a recarga
    // acumularia credito e cuspiria dois tiros no mesmo frame ao terminar.
    const busy = this.state === 'reloading' || this.state === 'switching';
    const interval = this._shotInterval();
    this._fireTimer -= dt;
    if (!held || busy) {
      if (this._fireTimer < 0) this._fireTimer = 0;
    } else if (this._fireTimer < -interval) {
      this._fireTimer = -interval;
    }

    if (!held || busy) return;

    if (this.ammo <= 0) {
      // clique seco: uma vez por aperto, com intervalo minimo entre repeticoes
      if (this._triggerReleased && this._dryTimer <= 0) {
        this._triggerReleased = false;
        this._dryTimer = DRY_COOLDOWN;
        this.ctx.events.emit(EVENTS.WEAPON_DRY, { weapon: w });
        this.ctx.events.emit(EVENTS.SFX, { name: w.sound.dry, volume: 0.6 });
        // recarrega sozinho: ninguem quer ficar preso no clique seco
        this._startReload();
      }
      return;
    }

    let shots = 0;
    while (this._fireTimer <= 0 && shots < MAX_SHOTS_PER_FRAME) {
      if (w.fireMode === 'semi' && !this._triggerReleased) break;
      if (this.ammo <= 0) break;
      this._fire();
      this._fireTimer += interval;
      shots++;
      if (w.fireMode === 'semi') {
        this._triggerReleased = false;   // semiauto exige soltar o botao
        break;
      }
    }
  }

  _fire() {
    const ctx = this.ctx;
    const w = this.current;
    const cam = ctx.camera;

    // A camera e a UNICA fonte de verdade do apontamento; ela ja carrega o
    // offset de recuo aplicado pelo CameraRig. Ver cabecalho deste arquivo.
    cam.getWorldPosition(_origin);
    cam.getWorldDirection(_dir);
    _origin.addScaledVector(_dir, 0.18);   // nasce a frente da capsula do jogador

    // A dispersao e lida ANTES de gastar a bala e ANTES de aplicar o recuo:
    // e isso que faz o primeiro tiro do padrao ser o tiro perfeito.
    const spread = this.getSpread();

    this.ammo = this.ammo - 1;
    this._sinceShot = 0;
    this.state = 'firing';

    this._resolveMuzzle(_muzzle, cam);

    // --- 1. balistica -------------------------------------------------------
    _fireParams.origin = _origin;
    _fireParams.dir = _dir;
    _fireParams.weapon = w;
    _fireParams.shooter = ctx.player || null;
    _fireParams.team = TEAM.PLAYER;
    _fireParams.spread = spread;
    _fireParams.damage = w.damage;
    _fireParams.range = w.range;
    _fireParams.pellets = w.pellets;
    _fireParams.penetration = w.penetration;
    _fireParams.falloff = w.damageFalloff;
    _fireParams.fromPlayer = true;
    try {
      ctx.ballistics?.fire?.(_fireParams);
    } catch (err) {
      warnOnce('ballistics.fire', err);
    }

    // --- 2. clarao na boca do cano -----------------------------------------
    _flashParams.position = _muzzle;
    _flashParams.direction = _dir;
    _flashParams.scale = w.muzzleScale;
    try {
      ctx.fx?.muzzleFlash?.(_flashParams);
    } catch (err) {
      warnOnce('fx.muzzleFlash', err);
    }

    // --- 3. recuo (depois do tiro, nunca antes) -----------------------------
    this._applyRecoilStep();

    // --- 4. dispersao acumulada --------------------------------------------
    const bloom = this._bloom + w.spreadPerShot;
    this._bloom = bloom > w.spreadMax ? w.spreadMax : bloom;

    // --- 5. eventos ---------------------------------------------------------
    // Excecao consciente a regra de zero alocacao: `origin` e `dir` sao clonados
    // porque a IA e o audio guardam o payload alem do frame. Sao dois objetos
    // por TIRO (no maximo ~16/s), nunca por frame.
    ctx.events.emit(EVENTS.WEAPON_FIRE, {
      weapon: w,
      origin: _origin.clone(),
      dir: _dir.clone(),
      ammoLeft: this.ammo,
      fromPlayer: true
    });
    // A propria arma do jogador nao e espacializada (sem `position`): ela sai
    // em 2D, no centro, como em qualquer FPS.
    ctx.events.emit(EVENTS.SFX, {
      name: w.sound.fire,
      volume: 1,
      pitch: 0.97 + Math.random() * 0.06
    });

    this._shotIndex++;
  }

  /**
   * Aplica um passo do padrao de recuo. Deterministico: o passo N e sempre o
   * mesmo, com um ruido pequeno por cima para nao parecer maquina. E o que
   * permite ao jogador DECORAR a rajada em vez de reagir a sorte.
   */
  _applyRecoilStep() {
    const w = this.current;
    const pat = w.recoilPattern;
    const i = this._shotIndex < pat.length ? this._shotIndex : pat.length - 1;
    const step = pat[i];
    const jitter = w.recoilJitter || 0;

    let scale = 1;
    const a = this._adsProgress;
    if (a > 0) scale *= lerp(1, w.adsRecoilScale, a);

    const c = this.ctx.controller;
    if (c) {
      if (c.crouching) scale *= w.crouchRecoilScale;
      if (c.grounded === false) scale *= w.airRecoilScale;
      // atirar andando custa um pouco mais de coice: premia quem para para mirar
      scale *= 1 + clamp01(this._horizontalSpeed() / PLAYER.WALK_SPEED) * 0.18;
    }

    const pitch = (step.pitch + (Math.random() * 2 - 1) * jitter) * DEG * scale;
    const yawRight = (step.yaw + (Math.random() * 2 - 1) * jitter) * DEG * scale;

    // No dado, yaw positivo puxa para a DIREITA da tela. No three, rotation.y
    // positivo gira para a ESQUERDA - por isso o sinal inverte aqui.
    this._recoil.y += pitch;
    this._recoil.x -= yawRight;

    const k = w.kickScale;
    try {
      this.ctx.cameraRig?.addRecoil?.(pitch * k, -yawRight * k);
    } catch (err) {
      warnOnce('cameraRig.addRecoil', err);
    }

    const sh = w.shake;
    if (sh) {
      try {
        this.ctx.cameraRig?.addShake?.(sh.intensity, sh.duration);
      } catch (err) {
        warnOnce('cameraRig.addShake', err);
      }
    }
  }

  /**
   * Volta do recuo ao centro. Tres regimes, e a diferenca entre eles e o que
   * faz a rajada longa ser dura e a rajada curta ser confortavel:
   *   dentro da rajada -> quase nao volta (o padrao se acumula de verdade)
   *   logo apos soltar -> segura no topo por um instante (peso)
   *   depois disso     -> volta rapido e suave ao centro
   */
  _updateRecoil(dt) {
    const r = this._recoil;
    if (r.x === 0 && r.y === 0) return;

    const rec = this.current.recoilRecovery;
    const burst = this._shotInterval() * BURST_WINDOW_FACTOR;

    let k;
    if (this._sinceShot < burst) k = rec.speed * rec.firingScale;
    else if (this._sinceShot < burst + rec.delay) k = rec.speed * 0.25;
    else k = rec.speed;

    const f = Math.exp(-k * dt);
    r.x *= f;
    r.y *= f;
    if (r.x < RECOIL_EPSILON && r.x > -RECOIL_EPSILON) r.x = 0;
    if (r.y < RECOIL_EPSILON && r.y > -RECOIL_EPSILON) r.y = 0;
  }

  _updateBloom(dt) {
    if (this._bloom <= 0) return;
    this._bloom *= Math.exp(-this.current.spreadRecovery * dt);
    if (this._bloom < 1e-6) this._bloom = 0;
  }

  // =========================================================================
  // Recarga
  // =========================================================================

  _readReloadInput() {
    if (this.ctx.input.wasPressed('reload')) this._startReload();
  }

  _startReload() {
    if (!this._ready || !this._isAlive()) return false;
    if (this.state === 'reloading' || this.state === 'switching') return false;
    const w = this.current;
    if (this.ammo >= w.magazine) return false;
    if (this.reserve <= 0) return false;

    // Pente vazio custa mais caro: ainda e preciso engatilhar.
    const empty = this.ammo <= 0;
    const duration = empty ? w.reloadEmptyTime : w.reloadTime;

    this.state = 'reloading';
    this._stateTimer = duration;
    this._reloadEmpty = empty;
    this._shotIndex = 0;          // recarregar devolve o padrao ao tiro 1
    this._triggerReleased = false; // nao dispara sozinho no instante que terminar

    this.ctx.events.emit(EVENTS.WEAPON_RELOAD_START, { weapon: w, duration, empty });
    this.ctx.events.emit(EVENTS.SFX, {
      name: empty ? w.sound.reloadEmpty : w.sound.reload,
      volume: 0.8
    });
    return true;
  }

  _finishReload() {
    const w = this.current;
    const need = w.magazine - this.ammo;
    const take = need < this.reserve ? need : this.reserve;
    if (take > 0) {
      this.ammo = this.ammo + take;
      this.reserve = this.reserve - take;
    }
    this.state = 'idle';
    this._stateTimer = 0;
    this.ctx.events.emit(EVENTS.WEAPON_RELOAD_END, {
      weapon: w, ammo: this.ammo, reserve: this.reserve, cancelled: false
    });
    this.ctx.events.emit(EVENTS.SFX, { name: w.sound.reloadEnd, volume: 0.7 });
  }

  /** Recarga interrompida: nada de municao creditada. */
  _cancelReload() {
    if (this.state !== 'reloading') return;
    const w = this.current;
    this.state = 'idle';
    this._stateTimer = 0;
    this.ctx.events.emit(EVENTS.WEAPON_RELOAD_END, {
      weapon: w, ammo: this.ammo, reserve: this.reserve, cancelled: true
    });
  }

  // =========================================================================
  // Troca de arma
  // =========================================================================

  _readSwitchInput() {
    const input = this.ctx.input;

    // Selecao direta por numero. O laco cobre quantas armas existirem no
    // arsenal, ate 9 - escrever caso a caso foi o que deixou as armas 4 a 7
    // inalcancaveis quando o arsenal cresceu de 3 para 7.
    const total = Math.min(this.weapons.length, 9);
    for (let i = 0; i < total; i++) {
      if (input.wasPressed('weapon' + (i + 1))) { this._requestSwitch(i); return; }
    }

    // Q = troca rapida para a ULTIMA usada (convencao de FPS, vale mais que
    // percorrer a lista no meio de um tiroteio).
    if (input.wasPressed('nextWeapon')) { this._requestSwitch(this._lastIndex); return; }

    // X e o botao TROCAR ARMA do celular percorrem o arsenal em ordem.
    if (input.wasPressed('cycleWeapon')) { this._cycle(1); return; }

    // Roda do mouse: um passo por entalhe, no sentido do giro.
    if (typeof input.consumeWheel === 'function') {
      const w = input.consumeWheel();
      if (w) this._cycle(w > 0 ? 1 : -1);
    }
  }

  /** Anda `passo` posicoes no arsenal, dando a volta nas pontas. */
  _cycle(passo) {
    const n = this.weapons.length;
    if (n <= 1) return false;
    const alvo = ((this._index + passo) % n + n) % n;
    return this._requestSwitch(alvo);
  }

  _requestSwitch(i) {
    if (!this._ready || !this._isAlive()) return false;
    if (!(i >= 0) || i >= this.weapons.length) return false;
    if (i === this._index) return false;
    if (this.state === 'switching') return false;

    // Trocar de arma e a UNICA forma de cancelar a recarga.
    if (this.state === 'reloading') this._cancelReload();

    const cur = this.current;
    const next = this.weapons[i];

    this._pendingIndex = i;
    this._switchOut = Math.max(0.05, cur.switchTime * 0.42);   // guardar a atual
    this._switchIn = Math.max(0.05, next.switchTime * 0.58);   // sacar a nova
    this._stateTimer = this._switchOut + this._switchIn;
    this._swapped = false;
    this.state = 'switching';

    this.isADS = false;
    this.aimState = 'hip';
    if (this._adsPrev) {
      this._adsPrev = false;
      this.ctx.events.emit(EVENTS.WEAPON_ADS, { weapon: cur, active: false });
    }

    // Anunciado no INICIO, com os tempos, para o ViewModel poder animar guardar
    // e sacar. A troca efetiva do modelo acontece em _commitSwitch().
    this.ctx.events.emit(EVENTS.WEAPON_SWITCH, {
      weapon: next, previous: cur, outTime: this._switchOut, inTime: this._switchIn
    });
    this.ctx.events.emit(EVENTS.SFX, { name: next.sound.switch, volume: 0.65 });
    return true;
  }

  _commitSwitch() {
    this._lastIndex = this._index;
    this._index = this._pendingIndex;
    this._resetWeaponState();
    try {
      this.ctx.viewModel?.setWeapon?.(this.current);
    } catch (err) {
      warnOnce('viewModel.setWeapon', err);
    }
  }

  /** Zera tudo que e "da rajada atual" - usado na troca e no respawn. */
  _resetWeaponState() {
    this._recoil.set(0, 0);
    this._bloom = 0;
    this._shotIndex = 0;
    this._fireTimer = 0;
    this._sinceShot = 999;
    this._dryTimer = 0;
    this._triggerHeld = false;
    this._triggerReleased = false;   // exige um novo aperto depois da troca
  }

  // =========================================================================
  // Auxiliares
  // =========================================================================

  /**
   * Ponto de saida da bala para efeito visual. Preferimos o ponto real do modelo
   * em primeira pessoa; se o ViewModel ainda nao existir ou devolver lixo, cai
   * para o offset da arma em espaco de camera.
   */
  _resolveMuzzle(out, cam) {
    const vm = this.ctx.viewModel;
    if (vm && typeof vm.getMuzzleWorldPosition === 'function') {
      try {
        const r = vm.getMuzzleWorldPosition(out);
        if (r && r !== out && r.isVector3) out.copy(r);
        if (Number.isFinite(out.x) && Number.isFinite(out.y) && Number.isFinite(out.z)
          && out.lengthSq() > 1e-8) {
          return out;
        }
      } catch (err) {
        warnOnce('viewModel.getMuzzleWorldPosition', err);
      }
    }
    const mo = this.current.muzzleOffset;
    const a = this._adsProgress;
    // na mira a arma vem para o centro da tela, e a boca do cano vai junto
    out.set(lerp(mo[0], 0, a), lerp(mo[1], -0.03, a), mo[2]);
    // transformada MUNDIAL: o CameraRig pode pendurar a camera em um pai, e ai
    // cam.position/cam.quaternion sozinhos dariam um ponto errado.
    cam.getWorldQuaternion(_camQuat);
    cam.getWorldPosition(_camPos);
    out.applyQuaternion(_camQuat).add(_camPos);
    return out;
  }

  _horizontalSpeed() {
    const c = this.ctx.controller;
    if (!c) return 0;
    const v = c.velocity;
    if (v && typeof v.x === 'number' && typeof v.z === 'number') {
      return Math.sqrt(v.x * v.x + v.z * v.z);
    }
    if (typeof c.speed === 'number') return c.speed;
    return 0;
  }

  _isAlive() {
    const p = this.ctx.player;
    return p ? p.alive !== false : true;
  }

  // =========================================================================
  // Reacao a eventos do jogo
  // =========================================================================

  _onGameState(payload) {
    const state = payload && payload.state;
    const previous = this._gameState;
    this._gameState = state;
    this.enabled = state === 'playing';

    if (state !== 'playing') {
      this.isADS = false;
      this.aimState = 'hip';
      this._adsPrev = false;
      this._triggerHeld = false;
      this._triggerReleased = false;
      return;
    }
    // Partida nova (nao apenas voltar da pausa) devolve o arsenal cheio.
    if (previous == null || previous === 'menu' || previous === 'gameover' || previous === 'victory') {
      this.resetLoadout();
    }
  }

  _onDeath() {
    if (this.state === 'reloading') this._cancelReload();
    this.state = 'idle';
    this._stateTimer = 0;
    this._swapped = true;
    this.isADS = false;
    this.aimState = 'hip';
    this._adsPrev = false;
    this._adsProgress = 0;
    this.adsProgress = 0;
    this._raiseTimer = 0;
    this._resetWeaponState();
  }
}
